import {CopilotChatSidePanel as SharedCopilotChatSidePanel} from '@github-ui/copilot-chat-side-panel/CopilotChatSidePanel'
import {CopilotSidePanelBridgeProvider} from '@github-ui/copilot-chat-side-panel/CopilotSidePanelContext'
import {isFeatureEnabled, isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {memo, type ReactNode} from 'react'

import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'

export const CopilotChatSidePanel = memo(function CopilotChatSidePanelImpl() {
  const isOpen = useIsDockedPanelOpen(DockedPanel.CopilotChat)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {closeButtonRef, returnFocusRef} = useDockedPanelFocusRefs()
  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')
  const showConversationTitle = isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')

  // When the flag is OFF, omit `renderCompactBody` so the shared package falls
  // back to its built-in legacy `<Dialog>` in compact mode.
  const renderCompactBody = updatedPullRequestHeader
    ? ({body, headerTitle, headerActions}: {body: ReactNode; headerTitle: ReactNode; headerActions: ReactNode}) => (
        <>
          {headerTitle ? <CompactPanel.HeaderTitle>{headerTitle}</CompactPanel.HeaderTitle> : null}
          {headerActions ? <CompactPanel.HeaderActions>{headerActions}</CompactPanel.HeaderActions> : null}
          <CompactPanel.Content className="p-0">
            <Panel.DialogFill>{body}</Panel.DialogFill>
          </CompactPanel.Content>
        </>
      )
    : undefined

  return (
    <CopilotSidePanelBridgeProvider
      isOpen={isOpen}
      isCompact={isCompact}
      closePanel={closePanel}
      closeButtonRef={closeButtonRef}
      returnFocusRef={returnFocusRef}
    >
      <SharedCopilotChatSidePanel
        isOpen={isOpen}
        onClose={closePanel}
        toggleSidesheetRef={returnFocusRef}
        disablePortal
        showConversationTitle={showConversationTitle}
        renderDockedPanel={({body, headerTitle, headerActions}) => (
          <Panel panelName={DockedPanel.CopilotChat} headerTitle={headerTitle} headerActions={headerActions}>
            <Panel.Body className="p-0">
              <Panel.ViewportFill bottomInsetPx={16}>{body}</Panel.ViewportFill>
            </Panel.Body>
          </Panel>
        )}
        renderCompactBody={renderCompactBody}
      />
    </CopilotSidePanelBridgeProvider>
  )
})
